import React, { useEffect, useState } from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';

const OPEN_MS = 280;
const CLOSE_MS = 220;
// Lets a web caller act once the close animation has finished (see DateTimePickerModal.web).
export const SHEET_CLOSE_MS = CLOSE_MS;

/**
 * Web host for Sheet. react-native-modal has ref issues on web (see
 * components/dialog/Modal.web.js), so web uses the plain React Native Modal.
 * The Modal's own "slide" moved the dimmed backdrop up with the panel (a dark
 * curtain rising, the panel only arriving in the last frames) and closing
 * unmounted it at once. Here the backdrop fades and only the panel slides, on
 * open and on close, like react-native-modal on phones.
 */
const SheetModal = ({ isVisible, onClose, backdropColor, children }) => {
	// Stays true for the close animation after isVisible turns false.
	const [isMounted, setIsMounted] = useState(isVisible);

	useEffect(() => {
		if (isVisible) {
			setIsMounted(true);
			return undefined;
		}
		const timer = setTimeout(() => setIsMounted(false), CLOSE_MS);
		return () => clearTimeout(timer);
	}, [isVisible]);

	if (!isVisible && !isMounted) {
		return null;
	}

	return (
		<Modal visible={true} transparent={true} animationType="none" onRequestClose={onClose}>
			<View style={styles.container} pointerEvents={isVisible ? 'auto' : 'none'}>
				<Pressable
					style={[StyleSheet.absoluteFill, { backgroundColor: backdropColor }, isVisible ? styles.fadeIn : styles.fadeOut]}
					onPress={onClose}
					accessibilityRole="button"
					accessibilityLabel="Close"
				/>
				{/* Same box as the container, so the panel's percentage heights are unchanged; translateY(100%) starts it just below the screen. */}
				<View style={[styles.panelHost, isVisible ? styles.slideIn : styles.slideOut]} pointerEvents="box-none">
					{children}
				</View>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
	},
	panelHost: {
		...StyleSheet.absoluteFillObject,
		justifyContent: 'flex-end',
	},
	fadeIn: {
		opacity: 1,
		animationDuration: `${OPEN_MS}ms`,
		animationTimingFunction: 'ease-out',
		animationKeyframes: { '0%': { opacity: 0 }, '100%': { opacity: 1 } },
	},
	fadeOut: {
		opacity: 0,
		animationDuration: `${CLOSE_MS}ms`,
		animationTimingFunction: 'ease-in',
		animationKeyframes: { '0%': { opacity: 1 }, '100%': { opacity: 0 } },
	},
	// Decelerates into place (fast start, gentle stop) like a native sheet.
	slideIn: {
		transform: 'translateY(0%)',
		animationDuration: `${OPEN_MS}ms`,
		animationTimingFunction: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
		animationKeyframes: { '0%': { transform: 'translateY(100%)' }, '100%': { transform: 'translateY(0%)' } },
	},
	slideOut: {
		transform: 'translateY(100%)',
		animationDuration: `${CLOSE_MS}ms`,
		animationTimingFunction: 'ease-in',
		animationKeyframes: { '0%': { transform: 'translateY(0%)' }, '100%': { transform: 'translateY(100%)' } },
	},
});

export default SheetModal;
